import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { NAV_LINKS } from "@data/constants";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <nav
      aria-label="Main navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-nav border-b border-white/[0.06]" : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between content-px py-5">
        {/* Logo */}
        <Link
          to="/"
          className="no-underline font-logo text-4xl text-konecta-orange tracking-wide uppercase"
        >
          KONECTA
        </Link>

        {/* Desktop Nav Links — centered */}
        <ul className="hidden lg:flex gap-8 list-none absolute left-1/2 -translate-x-1/2">
          {NAV_LINKS.map((link) => (
            <li key={link.path}>
              <Link
                to={link.path}
                className={`text-[0.7rem] font-medium uppercase tracking-[0.15em] no-underline transition-colors duration-300 ${
                  location.pathname === link.path
                    ? "text-konecta-white"
                    : "text-konecta-muted hover:text-konecta-white"
                }`}
                {...(location.pathname === link.path && { "aria-current": "page" })}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>

        {/* Desktop CTA */}
        <Link
          to="/contact"
          className="hidden lg:inline-block btn-primary text-xs !py-3 !px-6"
        >
          Connect With Us
        </Link>

        {/* Hamburger button */}
        <button
          className={`lg:hidden relative flex flex-col items-center justify-center w-10 h-10 rounded-xl cursor-pointer transition-all duration-300 border ${
            mobileOpen
              ? "bg-konecta-orange/15 border-konecta-orange/40 shadow-[0_0_16px_rgba(244,129,32,0.2)]"
              : "bg-white/[0.04] border-white/10 hover:bg-white/[0.08] hover:border-white/20"
          }`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle navigation"
          aria-expanded={mobileOpen}
        >
          {/* Top bar */}
          <span
            className={`absolute block h-[1.5px] rounded-full transition-all duration-300 ease-in-out origin-center ${
              mobileOpen
                ? "w-[18px] rotate-45 bg-konecta-orange"
                : "w-[20px] -translate-y-[5.5px] bg-konecta-white"
            }`}
          />
          {/* Middle bar */}
          <span
            className={`absolute block h-[1.5px] rounded-full transition-all duration-200 ease-in-out ${
              mobileOpen
                ? "w-0 opacity-0 bg-konecta-orange"
                : "w-[14px] opacity-100 bg-konecta-white"
            }`}
          />
          {/* Bottom bar */}
          <span
            className={`absolute block h-[1.5px] rounded-full transition-all duration-300 ease-in-out origin-center ${
              mobileOpen
                ? "w-[18px] -rotate-45 bg-konecta-orange"
                : "w-[20px] translate-y-[5.5px] bg-konecta-white"
            }`}
          />
        </button>
      </div>

      {/* ── Mobile Menu Overlay ── */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation menu"
        className={`fixed inset-0 z-40 flex flex-col lg:hidden transition-all duration-500 ${
          mobileOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
        }`}
        style={{
          background: "rgba(8,8,8,0.97)",
          backdropFilter: "blur(48px)",
          WebkitBackdropFilter: "blur(48px)",
        }}
      >
        {/* Ambient glow */}
        <div className="absolute top-0 left-0 w-[320px] h-[320px] bg-konecta-orange/[0.05] blur-[100px] rounded-full pointer-events-none -translate-x-1/3 -translate-y-1/3" />
        <div className="absolute bottom-0 right-0 w-[240px] h-[240px] bg-konecta-orange/[0.03] blur-[80px] rounded-full pointer-events-none translate-x-1/3 translate-y-1/3" />

        {/* Top bar — logo + close */}
        <div className="flex items-center justify-between content-px py-5 relative z-10">
          <Link
            to="/"
            className="no-underline font-logo text-4xl text-konecta-orange tracking-wide uppercase"
            onClick={() => setMobileOpen(false)}
          >
            KONECTA
          </Link>
          <button
            className="flex flex-col items-center justify-center w-10 h-10 rounded-xl bg-white/[0.05] border border-white/[0.08] cursor-pointer hover:bg-konecta-orange/10 hover:border-konecta-orange/25 transition-all duration-200"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <span className="block w-[18px] h-[1.5px] bg-white/80 rounded-full rotate-45 translate-y-[0.75px]" />
            <span className="block w-[18px] h-[1.5px] bg-white/80 rounded-full -rotate-45 -translate-y-[0.75px]" />
          </button>
        </div>

        {/* Nav links */}
        <ul className="flex flex-col flex-1 justify-center content-px gap-0 list-none relative z-10">
          {NAV_LINKS.map((link, i) => {
            const isActive = location.pathname === link.path;
            return (
              <li
                key={link.path}
                className={`transition-all duration-500 ${
                  mobileOpen ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6"
                }`}
                style={{ transitionDelay: `${100 + i * 55}ms` }}
              >
                <Link
                  to={link.path}
                  className={`group flex items-center justify-between w-full py-5 border-b no-underline transition-all duration-200 ${
                    isActive
                      ? "border-konecta-orange/25 text-konecta-orange"
                      : "border-white/[0.06] text-white/60 hover:text-konecta-white hover:border-white/[0.12]"
                  }`}
                  onClick={() => setMobileOpen(false)}
                  {...(isActive && { "aria-current": "page" })}
                >
                  <div className="flex items-center gap-3">
                    {isActive && (
                      <span className="w-1.5 h-1.5 rounded-full bg-konecta-orange shrink-0" />
                    )}
                    <span
                      className={`font-heading font-bold uppercase tracking-wider leading-none transition-all duration-200 ${
                        isActive ? "text-2xl" : "text-xl group-hover:text-2xl"
                      }`}
                    >
                      {link.label}
                    </span>
                  </div>
                  <span
                    className={`text-base transition-all duration-200 ${
                      isActive
                        ? "text-konecta-orange"
                        : "text-white/20 group-hover:text-white/60 group-hover:translate-x-1"
                    } inline-block`}
                  >
                    →
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Bottom CTA */}
        <div
          className={`content-px pb-10 pt-6 relative z-10 transition-all duration-500 ${
            mobileOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
          style={{ transitionDelay: `${100 + NAV_LINKS.length * 55}ms` }}
        >
          <Link
            to="/contact"
            className="btn-primary block w-full text-center"
            onClick={() => setMobileOpen(false)}
          >
            Connect With Us
          </Link>
        </div>
      </div>
    </nav>
  );
}
